import { AuthHelper } from "@/components/pages/Auth/AuthHelper";
import { UserLoginContext } from "@/components/pages/Auth/UserLoginProvider";
import { NumberFormatCustom } from "@/components/StoreComponents/NumberFormatCustom";
import { reloadProfile } from "@/redux/actions";
import { useAppDispatch } from "@/redux/hooks";
import {
  formatPhone,
  getPhoneNumberMasking,
  validatePhone,
} from "@/utils/StringUtils";
import { Box, Button, styled, useTheme } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { CheckoutInputV2 } from "../../../../../CheckoutInputV2";
import { CheckoutButton } from "../../../components/CheckoutButton";
import { ButtonLoadingEllipsis } from "@/components/StoreComponents/StoreButton";
import OTPVerificationInput from "./OTPVerificationInput";

const Terms = () => {
  return (
    <Box
      display="flex"
      gap="8px"
      alignItems="flex-start"
      width="100%"
      fontSize="12px"
      lineHeight="18px"
      fontWeight="500"
      sx={{
        opacity: 0.5,
      }}
    >
      By continuing, you consent to receiving text messages about orders and
      marketing offers.
    </Box>
  );
};

const EnterPhoneNumber = ({
  phoneNumber,
  setPhoneNumber,
  setOtpSession,
}: {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  setOtpSession: (otpSession: { username: string } | undefined) => void;
}) => {
  const theme = useTheme();
  const { signIn, submittingSignIn, clearOtpError } =
    useContext(UserLoginContext);

  const submitPhoneNumber = () => {
    clearOtpError();
    const phoneNumberInput = formatPhone(phoneNumber, "e164");
    signIn(phoneNumberInput, false, (_, session) => {
      setOtpSession(session);
    });
  };

  return (
    <>
      <div className="title">Sign in or create an account</div>
      <Box display="flex" flexDirection="column" gap="16px" width="100%">
        <CheckoutInputV2
          label="Phone Number"
          name="phoneNumber"
          autoComplete="tel"
          value={phoneNumber}
          style={{
            minWidth: "300px",
          }}
          onChange={(e) => {
            const value = e.target.value;
            setPhoneNumber(value?.trim());
          }}
          autoFocus
          InputProps={{
            inputComponent: NumberFormatCustom as any,
            inputProps: {
              format: getPhoneNumberMasking(phoneNumber || ""),
            },
          }}
          sx={{ flex: 1 }}
        />
        <Terms />
        <CheckoutButton
          onClick={() => {
            submitPhoneNumber();
          }}
          disabled={submittingSignIn || !validatePhone(phoneNumber)}
        >
          {submittingSignIn ? (
            <ButtonLoadingEllipsis
              baseFontSize={16}
              customColor={theme.palette.primary.main}
            />
          ) : (
            "Continue"
          )}
        </CheckoutButton>
      </Box>
    </>
  );
};

const EnterOTP = ({
  phoneNumber,
  changePhoneNumber,
  otpSession,
  clearOtpSession,
  setOtpSession,
}: {
  phoneNumber: string;
  changePhoneNumber: () => void;
  otpSession: { username: string } | undefined;
  clearOtpSession: () => void;
  setOtpSession: (otpSession: { username: string } | undefined) => void;
}) => {
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [isSendingOTP, setIsSendingOTP] = useState<boolean>(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState<boolean>(false);
  const hasOtpSession = !!otpSession?.username;
  const lastSubmittedCodeRef = useRef<string>("");
  const dispatch = useAppDispatch();
  const {
    submittingSignIn,
    otpError,
    resendCoolDown,
    verifyOtp,
    resendOtp,
    clearOtpError,
  } = useContext(UserLoginContext);

  useEffect(() => {
    const OTP_EXPECTED_LENGTH = 6;
    if (
      verificationCode.length === OTP_EXPECTED_LENGTH &&
      verificationCode !== lastSubmittedCodeRef.current
    ) {
      console.log("verificationCode", verificationCode);
      lastSubmittedCodeRef.current = verificationCode;
      setIsVerifyingOTP(true);
      clearOtpError();
      verifyOtp(
        verificationCode,
        otpSession,
        () => {
          dispatch(reloadProfile());
          setIsVerifyingOTP(false);
        },
        () => {
          setIsVerifyingOTP(false);
        }
      );
    }
  }, [verifyOtp, verificationCode, otpSession, dispatch, clearOtpError]);

  useEffect(() => {
    // Check if the phone number entered is different from the one in the session; if so, clear the session
    if (
      hasOtpSession &&
      AuthHelper.getEmailForPhoneNumber(phoneNumber) !== otpSession.username
    ) {
      clearOtpSession();
    }
  }, [hasOtpSession, otpSession?.username, phoneNumber, clearOtpSession]);

  const resendOTP = () => {
    setIsSendingOTP(true);
    resendOtp(
      phoneNumber,
      false,
      (session) => {
        setOtpSession(session);
        setIsSendingOTP(false);
      },
      () => {
        setIsSendingOTP(false);
      }
    );
  };

  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ fontSize: "14px", color: "primary.main" }}>
          {formatPhone(phoneNumber)}
        </Box>
        <Button
          onClick={changePhoneNumber}
          variant="text"
          sx={{
            textTransform: "none",
            color: theme.palette.primary.main,
            textDecoration: "underline",
            fontSize: "14px",
            padding: "0",
            minWidth: "auto",
            opacity: 0.5,
            "&:hover": {
              textDecoration: "underline",
              backgroundColor: "transparent",
            },
          }}
          disabled={submittingSignIn}
        >
          Change
        </Button>
      </Box>

      <Box
        sx={{
          fontSize: "14px",
          color: "primary.main",
          marginBottom: "8px",
        }}
      >
        We've sent a verification code to the phone number you entered. Please
        enter the code to continue.
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          marginBottom: "16px",
        }}
      >
        <OTPVerificationInput
          value={verificationCode}
          onChange={(value) => {
            setVerificationCode(value);
            clearOtpError();
          }}
          maxLength={6}
          error={!!otpError}
          disabled={submittingSignIn || isSendingOTP || isVerifyingOTP}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Button
          onClick={() => {
            setVerificationCode("");
            clearOtpError();
            resendOTP();
          }}
          variant="text"
          sx={{
            textTransform: "none",
            color: theme.palette.primary.main,
            textDecoration: "underline",
            fontSize: "14px",
            padding: "0",
            minWidth: "auto",
            "&:hover": {
              textDecoration: "underline",
              backgroundColor: "transparent",
            },
          }}
          disabled={resendCoolDown > 0 || submittingSignIn}
        >
          Resend
        </Button>
      </Box>
    </Box>
  );
};

const SignInContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
  maxWidth: "500px",
  border: "1px solid",
  borderColor: theme.palette.divider,
  padding: "24px",
  borderRadius: "8px",
  transition: "all 0.3s ease",
  boxSizing: "border-box",
  alignSelf: "center",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    maxWidth: "400px",
    padding: 0,
    border: "none",
  },
}));

const MobileCheckoutSignIn = () => {
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const [otpSession, setOtpSession] =
    useState<{ username: string } | undefined>();

  if (phoneNumber && otpSession) {
    return (
      <SignInContainer>
        <EnterOTP
          phoneNumber={phoneNumber}
          changePhoneNumber={() => {
            setPhoneNumber("");
            setOtpSession(undefined);
          }}
          otpSession={otpSession}
          setOtpSession={setOtpSession}
          clearOtpSession={() => setOtpSession(undefined)}
        />
      </SignInContainer>
    );
  }

  return (
    <SignInContainer>
      <EnterPhoneNumber
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        setOtpSession={setOtpSession}
      />
    </SignInContainer>
  );
};

export default MobileCheckoutSignIn;
